import { FC, ReactNode, useRef, useState } from 'react';
import SearchField from 'further-ui/components/SearchField';
import {
  Box,
  MenuItem,
  MenuList,
  Popper,
  Skeleton,
  styled,
  Typography,
} from '@mui/material';

const Container = styled(Box)({
  width: '100%',
  position: 'relative',
});

const Options = styled(Popper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(theme.spacingSizes.xs, theme.spacingSizes.sm),
  width: '100%',
  zIndex: theme.zIndex.tooltip,
}));

const Option = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs),
  borderRadius: theme.shape.borderRadius,
}));

const NoResults = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xxxl, theme.spacingSizes.xs),
}));

type Props = {
  placeholder: string;
  options: Array<{
    value: string;
    label: ReactNode;
  }>;
  onOptionSelect: (value: string) => void;
  onSearch: (query: string) => void;
  isLoading: boolean;
  noResultsMessage?: string;
};

const DynamicSearchField: FC<Props> = ({
  placeholder,
  options,
  onSearch,
  onOptionSelect,
  isLoading,
  noResultsMessage,
}) => {
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const showOptions = options.length > 0 || isLoading || query.length > 0;

  return (
    <Container ref={containerRef}>
      <SearchField
        fullWidth
        value=""
        placeholder={placeholder}
        debounceTime={300}
        onSearch={(query) => {
          setQuery(query);
          onSearch(query);
        }}
        clearable={true}
        autoSearchOnDebounce
        allowEmptyQuery
      />
      {containerRef.current ? (
        <Options
          open={showOptions}
          placement="bottom"
          disablePortal={true}
          anchorEl={containerRef.current}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          {isLoading ? (
            <Box>
              <Skeleton />
            </Box>
          ) : options.length > 0 ? (
            <MenuList disablePadding>
              {options.map((option) => (
                <Option
                  key={option.value}
                  onClick={() => {
                    onOptionSelect(option.value);
                  }}
                >
                  {option.label}
                </Option>
              ))}
            </MenuList>
          ) : (
            <NoResults>
              <Typography variant="body2" textAlign="center">
                {noResultsMessage ?? `No results found for "${query}".`}
              </Typography>
            </NoResults>
          )}
        </Options>
      ) : null}
    </Container>
  );
};

export default DynamicSearchField;
